/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl-next';
import Button from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';
import { cssMap, jsx } from '@atlaskit/css';
import ArrowLeftIcon from '@atlaskit/icon/core/migration/arrow-left';
import WarningIcon from '@atlaskit/icon/core/migration/warning';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { type ExternalUser, type OptionData } from '@atlaskit/user-picker';
import Tooltip from '@atlaskit/tooltip';

import { useInviteeListStateContext } from '../InviteeList';
import { defaultInviteThirdPartyTeamsClient } from '../../clients';
import { SlackLogo } from '../icons/SlackLogo';
import { messages } from '../i18n';
import TeamMembersSkeleton from './TeamMembersSkeleton';
import { SLACK_SOURCE } from '../ThirdParty/constants';
import { type SlackUserDetails } from '../../types';
import { slackToExternalUser } from '../../utils';
import { useThirdPartyState } from '../ThirdParty';
import TeamMember from './TeamMember';
import { useAnalytics } from '../../analytics';
import { TeamMemberPickerAnalytics } from './TeamMemberPickerAnalytics';
import { getErrorDetails } from '../../clients/helpers';
import { useCoreInvitesJiraProjectContext } from '../../experiments/coreInvitesInJiraProjects/CoreInvitesJiraProjectContext';

const styles = cssMap({
	wrapper: {
		width: '354px',
	},
	header: {
		height: '56px',
		borderColor: token('color.border', '#091E4224'),
		borderStyle: 'solid',
		borderWidth: token('border.width', '1px'),
		borderStartStartRadius: token('border.radius.100', '4px'),
		borderStartEndRadius: token('border.radius.100', '4px'),
	},
	backButtonWrapper: {
		marginLeft: token('space.150', '12px'),
		flex: '0 0 auto',
	},
	continueButtonWrapper: {
		marginLeft: 'auto',
		marginRight: token('space.150', '12px'),
	},
	logoWrapper: {
		width: '32px',
		height: '32px',
	},
	teamNameWrapper: {
		marginInline: token('space.075', '6px'),
		minWidth: '0px',
	},
	selectAllButtonWrapper: {
		height: '48px',
	},
	teamMembers: {
		paddingInline: token('space.150', '12px'),
		paddingBlockEnd: token('space.150', '12px'),
		borderColor: token('color.border', '#091E4224'),
		borderStyle: 'solid',
		borderTopWidth: 0,
		borderBottomWidth: token('border.width', '1px'),
		borderInlineWidth: token('border.width', '1px'),
		borderEndStartRadius: token('border.radius.100', '4px'),
		borderEndEndRadius: token('border.radius.100', '4px'),
		maxHeight: '312px',
		overflow: 'auto',
		// @ts-ignore
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		"[data-testid='max-members-warning']": {
			marginLeft: token('space.negative.100', '-8px'),
		},
		// @ts-ignore
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		"[data-testid='max-members-warning--inline-dialog']": {
			maxWidth: '240px',
		},
	},
	pickItem: {
		height: '48px',
		marginInline: token('space.050', '4px'),
		// @ts-ignore
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		label: {
			alignItems: 'center',
		},
	},
});

export type Props = {
	tenantId: string;
	product: string;
	team: OptionData;
	clearTeamSelection?: () => void;
};

const TeamMemberPicker = (props: Props) => {
	const analytics = useAnalytics(TeamMemberPickerAnalytics);

	const { selectedSlackWorkspace, getUserByThirdPartyId, cacheUserByThirdPartyId } =
		useThirdPartyState();

	const { isCoreInvitesInJiraProjectEnabled } = useCoreInvitesJiraProjectContext();

	const { setSelectedTeam, userPickerOptions, selectUsersFromSlackTeam } =
		useInviteeListStateContext();
	const originalUserPickerSelectedIds = new Set(userPickerOptions.map((user) => user.id));

	const [userPickerSelectedIds, setUserPickerSelectedIds] = useState<Set<string>>(
		originalUserPickerSelectedIds,
	);
	const [teamMembers, setTeamMembers] = useState<ExternalUser[]>([]);
	const [isTeamMemberLoading, setIsTeamMemberLoading] = useState<boolean>(true);
	const [hasMoreMembersThan, setMoreMembersThan] = useState(0);

	const teamMembersSelected = teamMembers.filter((member) => userPickerSelectedIds.has(member.id));

	useEffect(() => {
		const workspaceId = selectedSlackWorkspace?.id;

		const getChannelMembers = async (workspaceId: string, channelId: string): Promise<void> => {
			try {
				setIsTeamMemberLoading(true);
				const slackChannelMembersResponse =
					await defaultInviteThirdPartyTeamsClient.getSlackChannelMembers(workspaceId, channelId);

				const slackChannelMembers = slackChannelMembersResponse.members.map((id) => ({
					id,
					cachedData: getUserByThirdPartyId(SLACK_SOURCE, id),
				}));

				const slackUserDetailsAndPermsResponse =
					await defaultInviteThirdPartyTeamsClient.getSlackUserDetailsAndPerms(
						props.tenantId,
						workspaceId,
						props.product,
						slackChannelMembers
							.filter((user) => user.cachedData !== null)
							.map((user) => ({
								id: user.id,

								// Note that experiment-api won't make the Slack request for users that have emails defined, this is how we
								// are able to reduce the number of users being fetched using the cache
								//
								// https://bitbucket.org/atlassian/experiment-api/src/b01f7e2cdba0020ebcd2d3da6464b39075353e4e/src/experiments/invite-3rd-party-teams/getSlackUserDetailsAndPerms.ts#lines-18
								email: user.cachedData?.email,
							})),
					);

				const invalidUserIds = new Set(
					slackChannelMembers.filter((user) => !user.cachedData?.email).map((user) => user.id),
				);

				const slackUserDetailsAndPermsMap: Map<
					string,
					{
						hasProductAccess: boolean;
						details?: SlackUserDetails;
					}
				> = new Map();

				for (const userDetails of slackUserDetailsAndPermsResponse.userDetailsAndPerms) {
					slackUserDetailsAndPermsMap.set(userDetails.id, {
						hasProductAccess: userDetails.hasProductAccess,
						details: userDetails.details,
					});
					invalidUserIds.delete(userDetails.id);

					if (userDetails.details) {
						cacheUserByThirdPartyId(
							SLACK_SOURCE,
							userDetails.id,

							// The type: 'directMessage' is purely for type purposes, since the interface SlackUser has a field type: 'directMessage', so
							// it needs to be set here to satisfy the types. slackToExternalUser() doesn't actually uses slackUser.type so it won’t cause any problems
							//
							// https://bitbucket.org/atlassian/atlassian-frontend/src/dabb025dcab4f47f956f61985cd57195a8d8a9d2/packages/growth/integrations/src/share-to-slack/common/types.ts#lines-129
							slackToExternalUser({
								...userDetails.details,
								type: 'directMessage',
							}),
						);
					}
				}

				for (const userId of invalidUserIds) {
					cacheUserByThirdPartyId(SLACK_SOURCE, userId, null);
				}

				const teamMembers = slackChannelMembers
					.filter((user) => !invalidUserIds.has(user.id))
					.map(({ id, cachedData }) => {
						const userDetails = slackUserDetailsAndPermsMap.get(id);

						const teamMember: ExternalUser = {
							id,
							email: cachedData?.email ?? userDetails?.details?.email,
							avatarUrl: cachedData?.avatarUrl ?? userDetails?.details?.avatarUrl,

							// Using empty string as a last resort to satisfy the type of `name`, which is strictly `string`. Though it should never be used since `userDetails` should contain the details
							// in case the cache doesn't have it
							name: cachedData?.name ?? userDetails?.details?.realName ?? '',
							hasProductAccess: userDetails?.hasProductAccess,
							sources: ['slack'],
						};

						return teamMember;
					});

				analytics.teamMemberPickerViewed({
					teamId: channelId,
					members: teamMembers.length,
					membersWithAccess: teamMembers.filter((member) => member.hasProductAccess).length,
					membersCapped: slackChannelMembersResponse.hasMoreMembers,
				});

				setTeamMembers(teamMembers);
				setMoreMembersThan(slackChannelMembersResponse.hasMoreMembers ? teamMembers.length : 0);
			} catch (err) {
				const { code, message, status } = getErrorDetails(err);
				analytics.viewSlackTeamMembersFailed(code, message, status);
			} finally {
				setIsTeamMemberLoading(false);
			}
		};

		if (workspaceId) {
			getChannelMembers(workspaceId, props.team.id);
		}
	}, [
		analytics,
		cacheUserByThirdPartyId,
		getUserByThirdPartyId,
		props.product,
		props.team,
		props.tenantId,
		selectedSlackWorkspace,
	]);

	const isNotPreSelected = (member: { id: string }) =>
		!originalUserPickerSelectedIds.has(member.id);

	const isSelectedMembersChanged =
		originalUserPickerSelectedIds.size !== userPickerSelectedIds.size ||
		teamMembersSelected.some(isNotPreSelected);

	const selectableTeamMembers = teamMembers.filter(
		(teamMember) =>
			// team member is always selectable when `core invites in jira project` is enabled, regardless of its product access
			isCoreInvitesInJiraProjectEnabled || !teamMember.hasProductAccess,
	);

	const selectAllInTeam = useCallback(
		() =>
			setUserPickerSelectedIds(new Set(selectableTeamMembers.map((teamMember) => teamMember.id))),
		[selectableTeamMembers],
	);

	const deselectAllInTeam = useCallback(() => setUserPickerSelectedIds(new Set()), []);

	const onChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			if (event.target.checked) {
				if (!userPickerSelectedIds.has(event.target.value)) {
					setUserPickerSelectedIds(new Set(userPickerSelectedIds.add(event.target.value)));
				}
			} else {
				if (userPickerSelectedIds.has(event.target.value)) {
					userPickerSelectedIds.delete(event.target.value);
					setUserPickerSelectedIds(new Set(userPickerSelectedIds));
				}
			}
		},
		[userPickerSelectedIds],
	);

	const onContinue = useCallback(() => {
		analytics.teamMemberPickerContinueClicked({
			teamId: props.team.id,
			members: teamMembers.length,
			membersWithAccess: teamMembers.filter((member) => member.hasProductAccess).length,
			selectedMembers: userPickerSelectedIds.size,
			membersCapped: hasMoreMembersThan > 0,
		});

		selectUsersFromSlackTeam(teamMembersSelected, teamMembers);
		setSelectedTeam(undefined);
	}, [
		analytics,
		props.team.id,
		teamMembers,
		userPickerSelectedIds.size,
		hasMoreMembersThan,
		selectUsersFromSlackTeam,
		setSelectedTeam,
		teamMembersSelected,
	]);

	const isTeamMemberDisabled = useCallback(
		(member: ExternalUser) =>
			// team member is always selectable when `core invites in jira project` is enabled, regardless of its product access
			isCoreInvitesInJiraProjectEnabled ? false : !!member.hasProductAccess,
		[isCoreInvitesInJiraProjectEnabled],
	);

	const isTeamMemberChecked = useCallback(
		(member: ExternalUser) =>
			// team member is always checkable when `core invites in jira project` is enabled, regardless of its product access
			isCoreInvitesInJiraProjectEnabled
				? userPickerSelectedIds.has(member.id)
				: userPickerSelectedIds.has(member.id) || member.hasProductAccess,
		[isCoreInvitesInJiraProjectEnabled, userPickerSelectedIds],
	);

	const showTeamMemberSubText = useCallback(
		(member: ExternalUser) =>
			// team member should never show sub text when `core invites in jira project` is enabled, regardless of its product access
			isCoreInvitesInJiraProjectEnabled ? false : !member.hasProductAccess,
		[isCoreInvitesInJiraProjectEnabled],
	);

	const { formatMessage } = useIntl();

	return (
		<Box
			testId="team-member-picker-wrapper"
			backgroundColor="color.background.input"
			xcss={styles.wrapper}
		>
			<Inline alignBlock="center" spread="space-between" xcss={styles.header}>
				<Box xcss={styles.backButtonWrapper}>
					<Button
						appearance="subtle"
						iconBefore={
							<ArrowLeftIcon
								color="currentColor"
								spacing="spacious"
								label={formatMessage(messages.inviteeListLabelThirdPartyTeamMemberPickerBackButton)}
							/>
						}
						onClick={props.clearTeamSelection}
					/>
				</Box>
				<Box xcss={styles.logoWrapper}>
					<SlackLogo />
				</Box>
				<Inline xcss={styles.teamNameWrapper}>
					<Tooltip content={props.team?.name}>
						<Text maxLines={1}>{props.team?.name}</Text>
					</Tooltip>
				</Inline>
				<Box xcss={styles.continueButtonWrapper}>
					<Button
						appearance="primary"
						isDisabled={isTeamMemberLoading || !isSelectedMembersChanged}
						onClick={onContinue}
						testId="team-member-picker-continue-button"
					>
						<FormattedMessage
							{...messages.inviteeListLabelThirdPartyTeamMemberPickerContinueButton}
						/>
					</Button>
				</Box>
			</Inline>
			{isTeamMemberLoading ? (
				<TeamMembersSkeleton />
			) : (
				teamMembers && (
					<Box testId="team-members" xcss={styles.teamMembers}>
						<Inline alignBlock="center" xcss={styles.selectAllButtonWrapper}>
							{teamMembersSelected.length ? (
								<Button
									testId="team-member-picker-deselect-all"
									appearance="link"
									onClick={deselectAllInTeam}
								>
									<FormattedMessage
										{...messages.thirdPartyTeamDeselectAllLabel}
										values={{ numSelected: teamMembersSelected.length }}
									/>
								</Button>
							) : (
								<Button
									testId="team-member-picker-select-all"
									appearance="link"
									onClick={selectAllInTeam}
								>
									<FormattedMessage
										{...messages.thirdPartyTeamSelectAllLabel}
										values={{
											numSelectable: selectableTeamMembers.length,
										}}
									/>
								</Button>
							)}
							{hasMoreMembersThan > 0 && (
								<Tooltip
									content={formatMessage(messages.thirdPartyTeamHasMoreMembersWarningText, {
										memberCount: hasMoreMembersThan,
									})}
								>
									<Button
										testId="max-members-warning"
										appearance="link"
										iconBefore={
											<WarningIcon
												color="currentColor"
												spacing="spacious"
												label={formatMessage(messages.thirdPartyTeamHasMoreMembersWarningLabel)}
											/>
										}
									/>
								</Tooltip>
							)}
						</Inline>
						{teamMembers.map((member) => {
							const isDisabled = isTeamMemberDisabled(member);

							return (
								<Inline key={member.id} alignBlock="center" xcss={styles.pickItem}>
									<Checkbox
										isDisabled={isDisabled}
										onChange={onChange}
										isChecked={isTeamMemberChecked(member)}
										value={member.id}
										label={
											<TeamMember
												isDisabled={isDisabled}
												member={member}
												showSubText={showTeamMemberSubText(member)}
											/>
										}
										testId={`team-member-${member.id}`}
									/>
								</Inline>
							);
						})}
					</Box>
				)
			)}
		</Box>
	);
};

export default TeamMemberPicker;
